import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, drawnFrameX, footprintCorner, type ManifestPixelOffset, type PropFootprint } from "daedalus-core";
import { groundRise, thingGroundY } from "../collision/things.ts";
import { AUTHORING_CELL, SIXTEENTHS } from "../movement/step.ts";

/**
 * A shrine's ground point (ADR-096 decision 8): where it stands for every rule, the centre of the lowest row its
 * footprint blocks. An obelisk's frame reaches 3.5 tiles below its stone, so the frame's bottom, a thing's feet point,
 * is not where a player sees the shrine stand. The y is the training dummy's ground line (`thingGroundY`), so both
 * read one rounding; the x is laid on the collision cells as the footprint is (`footprintCorner`).
 */

/**
 * A shrine thing as its ground point is read: its feet point in sixteenths, the footprint of the state it rests in
 * (null for none), the mirroring of its prop, and its appearance's pixel offset (undefined for none).
 */
export interface ShrineStanding {
  readonly x: number;
  readonly y: number;
  readonly footprint: PropFootprint | null;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly offset?: ManifestPixelOffset | undefined;
}

/**
 * The middle of the lowest row a footprint blocks, in pixels from the frame's left edge: half way between the first
 * column and the column past the last that the runs of that row cover.
 *
 * @param footprint A footprint with at least one run.
 * @param flipX Whether the frame is mirrored, which mirrors the columns across it.
 * @returns The middle, in pixels.
 */
function lowestRowCentre(footprint: PropFootprint, flipX: boolean): number {
  let lowest = Number.POSITIVE_INFINITY;
  let first = 0;
  let last = 0;
  for (const run of footprint.runs) {
    if (run[0] < lowest) {
      lowest = run[0];
      first = run[1];
      last = run[2];
    } else if (run[0] === lowest) {
      first = Math.min(first, run[1]);
      last = Math.max(last, run[2]);
    }
  }
  const centre = (first + last + 1) / 2;
  return flipX ? footprint.w - centre : centre;
}

/**
 * Where a shrine stands: the centre of the lowest row its footprint blocks, on the cells the stamp lays that row on.
 * A shrine that stamps nothing (no footprint, no runs, or mirrored upright) stands on its feet point.
 *
 * @param thing The shrine.
 * @param tileSize Tile size in pixels.
 * @returns The ground point in sixteenths, whole numbers for a feet point of whole numbers.
 */
export function shrineGround(thing: ShrineStanding, tileSize: number): { x: number; y: number } {
  const footprint = thing.footprint;
  if (thing.flipY || footprint === null || footprint.runs.length === 0) {
    return { x: thing.x, y: thing.y };
  }
  const left = footprintCorner(drawnFrameX(thing.x / SIXTEENTHS, footprint.w, thing.offset?.x ?? 0, tileSize) * tileSize, 0, footprint.w, thing.flipX, tileSize / COMPILED_COLLISION_CELLS_PER_TILE).left;
  return { x: Math.floor(((left + lowestRowCentre(footprint, thing.flipX)) * SIXTEENTHS) / tileSize), y: thingGroundY(thing.y, footprint, false, tileSize, thing.offset?.y) };
}

/**
 * The ground point's cell in a frame's own space, as activation cells count it (`[column, rowsUp]`, column 0 on the
 * frame's left edge and row 0 the cell row above its bottom edge): the cell `shrineGround` falls in once the frame is
 * laid, so cells written around it lie around the ground point wherever the prop stands. A footprint that blocks
 * nothing has it under the middle of the frame's bottom edge, where its feet point is.
 *
 * @param footprint The frame's footprint: its size and, when it blocks, its runs.
 * @param tileSize Tile size in pixels.
 * @returns The cell, `rowsUp` -1 for the row under the frame.
 */
export function groundCellInFrame(footprint: PropFootprint, tileSize: number): { column: number; rowsUp: number } {
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  if (footprint.runs.length === 0) {
    return { column: Math.floor(footprint.w / 2 / cellPixels), rowsUp: -1 };
  }
  const rise = Math.round((groundRise(footprint) * SIXTEENTHS) / tileSize);
  return { column: Math.floor(lowestRowCentre(footprint, false) / cellPixels), rowsUp: Math.ceil(rise / AUTHORING_CELL) - 1 };
}

/**
 * The collision cell a ground point lies in.
 *
 * @param x The ground point's x in sixteenths.
 * @param y The ground point's y in sixteenths.
 * @returns The cell's column and row.
 */
export function shrineGroundCell(x: number, y: number): { column: number; row: number } {
  return { column: Math.floor(x / AUTHORING_CELL), row: Math.floor(y / AUTHORING_CELL) };
}

/**
 * Where the search for a standing spot at a shrine thing starts (ADR-096 decision 8): the feet point whose box fills
 * the cell row just south of the ground point's, under the ground point.
 *
 * @param x The ground point's x in sixteenths.
 * @param y The ground point's y in sixteenths.
 * @returns The feet point in sixteenths.
 */
export function shrineStandFrom(x: number, y: number): { x: number; y: number } {
  return { x, y: (Math.floor(y / AUTHORING_CELL) + 2) * AUTHORING_CELL };
}
